// Section 9: Closing Coordinator Assignment +
// Hand-off-to-Closing.
//
// Hand-off advances Deal.Stage to Closing atomically — the backend
// UnderwritingCertificationService.SignAsync for
// UW.S9.HandOffToClosing handles the stage transition + cert in one
// transaction. The button is gated client-side on B2 assigned + 2 auth
// certs signed; the backend re-checks on Sign.

import { useEffect, useMemo, useState } from "react";
import { Button, Card, FormField } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { RoleFilteredUserPicker } from "../../common/RoleFilteredUserPicker";
import { UnderwritingCertificationCheckbox } from "../../common/UnderwritingCertificationCheckbox";
import {
  useDealAssignments,
  useHandOffToClosing,
  useUnderwritingCertifications,
  useUpdateAssignment,
} from "@/hooks/dealFlow/useDealFlow";
import { useUndoSection9Handoff } from "@/hooks/dealFlow/useAdminUndo";
import { AdminUndoButton } from "../../admin/AdminUndoButton";

interface ClosingCoordinatorAssignmentSectionProps {
  dealId: string;
}

export function ClosingCoordinatorAssignmentSection({
  dealId,
}: ClosingCoordinatorAssignmentSectionProps) {
  const assignments = useDealAssignments(dealId);
  const updateAssignment = useUpdateAssignment(dealId);
  const certs = useUnderwritingCertifications(dealId, "Underwriting", 9);
  const handOff = useHandOffToClosing(dealId);
  const undoHandoff = useUndoSection9Handoff(dealId);
  const toast = useToast();

  const currentB2 = useMemo(
    () => assignments.data?.find((a) => a.roleCode === "B2"),
    [assignments.data],
  );
  const [b2UserId, setB2UserId] = useState<string | null>(null);

  useEffect(() => {
    setB2UserId(currentB2?.assignedUserId ?? null);
  }, [currentB2?.assignedUserId]);

  const onSaveB2 = async () => {
    if (!b2UserId) return;
    try {
      await updateAssignment.mutateAsync({
        roleCode: "B2",
        body: { status: "Assigned", assignedUserId: b2UserId },
      });
      toast.success("B2 assigned.");
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Could not assign B2.",
      );
    }
  };

  const b2Assigned = currentB2?.status === "Assigned" && !!currentB2.assignedUserId;
  const sectionsCompleteSigned = !!certs.data?.find(
    (c) => c.certificationCode === "UW.S9.SectionsComplete",
  );
  const authGrantedSigned = !!certs.data?.find(
    (c) => c.certificationCode === "UW.S9.EscrowAuthorizationGranted",
  );
  const handOffSigned = !!certs.data?.find(
    (c) => c.certificationCode === "UW.S9.HandOffToClosing",
  );

  const canHandOff =
    b2Assigned && sectionsCompleteSigned && authGrantedSigned && !handOffSigned;

  const onHandOff = async () => {
    try {
      await handOff.mutateAsync();
      toast.success("Hand-off to Closing complete. Deal advanced to Closing.");
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Could not hand off to Closing.",
      );
    }
  };

  return (
    <Card>
      <header className="mb-space-3">
        <h3 className="font-display text-h4 text-onyx m-0">
          Section 9 — Closing Coordinator Assignment
        </h3>
        <p className="text-caption text-gray-mid m-0">
          Assign B2 + sign the 2 authorization certs, then hand the deal
          off to the Closing phase.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-[2fr_auto] items-end gap-space-3 mb-space-4">
        <FormField label="B2 — Closing Coordinator">
          <RoleFilteredUserPicker
            role="B2"
            value={b2UserId}
            onChange={setB2UserId}
            disabled={handOffSigned}
          />
        </FormField>
        <Button
          variant="secondary"
          onClick={() => void onSaveB2()}
          disabled={!b2UserId || updateAssignment.isPending || handOffSigned}
        >
          {updateAssignment.isPending ? "Saving…" : "Save assignment"}
        </Button>
      </div>

      <ul className="flex flex-col gap-space-2 m-0 p-0 list-none mb-space-4">
        <li>
          <UnderwritingCertificationCheckbox
            dealId={dealId}
            tab="Underwriting"
            section={9}
            certificationCode="UW.S9.SectionsComplete"
            label="I confirm Sections 1–8 are complete and ready for hand-off."
          />
        </li>
        <li>
          <UnderwritingCertificationCheckbox
            dealId={dealId}
            tab="Underwriting"
            section={9}
            certificationCode="UW.S9.EscrowAuthorizationGranted"
            label="I authorize the assigned Closing Coordinator (B2) to release escrow."
          />
        </li>
      </ul>

      <div className="border-t border-border-light pt-space-3 flex justify-between items-center">
        <span className="text-caption text-gray-mid">
          {handOffSigned
            ? "Hand-off complete — deal is now in Closing."
            : canHandOff
              ? "Ready to hand off."
              : "Awaiting B2 assignment + both auth certs."}
        </span>
        <div className="flex items-center gap-space-3">
          {handOffSigned && (
            <AdminUndoButton
              label="Hand-off"
              modalTitle="Undo Section 9 Hand-off"
              modalConsequence={
                <>
                  This returns the deal to the Underwriting stage and clears
                  the Section 9 hand-off certification. The server refuses
                  the undo once a Closing-tab certification is certified,
                  declined or escalated, or the escrow is released. Closing
                  certifications saved as drafts are deleted with the
                  hand-off. Other Closing-tab work is NOT undone: the
                  servicer decision and payment-setup changes from Step 2
                  stay on the deal.
                </>
              }
              successToast="Section 9 hand-off undone. Deal returned to Underwriting."
              invoke={(reason) => undoHandoff.mutateAsync(reason)}
            />
          )}
          <Button
            variant="primary"
            onClick={() => void onHandOff()}
            disabled={!canHandOff || handOff.isPending}
            title={
              !canHandOff
                ? "B2 must be assigned and both authorization certs must be signed first."
                : undefined
            }
          >
            {handOff.isPending ? "Signing…" : handOffSigned ? "Handed off →" : "Hand off to Closing"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
